/**
 * DADDL modal overlay — the one dialog behaviour for the theme.
 * Used by snippets/daddl-overlay.liquid and by sections that keep their own
 * dialog markup (video viewers) but wrap it in <daddl-overlay>.
 *
 * - element.open(opener?) / element.close(), or toggle the `open` attribute.
 * - Open moves focus inside: [data-daddl-overlay-autofocus] first, else the
 *   first focusable, else the [data-daddl-overlay-dialog] node itself.
 * - Tab / Shift+Tab stay inside while open. Escape and any
 *   [data-daddl-overlay-dismiss] close it.
 * - Close fires `daddl-overlay:close`, then returns focus to `returnFocus`
 *   (the opener). Listeners may reassign `overlay.returnFocus` during that
 *   event when the opener is no longer the right target (e.g. a carousel
 *   that moved on while the viewer was open).
 */
class DaddlOverlay extends HTMLElement {
  connectedCallback() {
    /* Sections portal the overlay to <body>; reconnecting must not bind twice. */
    if (!this._bound) {
      this._bound = true;
      this.dialog = this.querySelector('[data-daddl-overlay-dialog]');
      this.onKey = (event) => {
        if (!this.hasAttribute('open')) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
        } else if (event.key === 'Tab') {
          this.trapFocus(event);
        }
      };
      this.querySelectorAll('[data-daddl-overlay-dismiss]').forEach((el) => {
        el.addEventListener('click', () => this.close());
      });
    }
    if (this.hasAttribute('open')) this.lock();
  }

  disconnectedCallback() {
    this.unlock();
  }

  static get observedAttributes() {
    return ['open'];
  }

  attributeChangedCallback(name, prev, next) {
    if (name !== 'open' || prev === next) return;
    if (next !== null) this.lock();
    else this.unlock();
  }

  open(opener) {
    this.returnFocus = opener || document.activeElement;
    this.setAttribute('open', '');
    this.focusInitial();
  }

  close() {
    if (!this.hasAttribute('open')) return;
    this.removeAttribute('open');
    this.dispatchEvent(new CustomEvent('daddl-overlay:close', { bubbles: true }));
    const target = this.returnFocus;
    this.returnFocus = null;
    if (target && target.isConnected && typeof target.focus === 'function') {
      target.focus({ preventScroll: true });
    }
  }

  /* Initial focus target. The dialog node is the fallback so focus never stays behind the backdrop. */
  focusInitial() {
    const preferred = this.querySelector('[data-daddl-overlay-autofocus]');
    let target = preferred && this.isFocusable(preferred) ? preferred : this.focusables()[0];
    if (!target && this.dialog) {
      if (!this.dialog.hasAttribute('tabindex')) this.dialog.setAttribute('tabindex', '-1');
      target = this.dialog;
    }
    if (!target) return;
    target.focus({ preventScroll: true });
    /* A skin that still fades visibility in leaves the target unfocusable this frame; retry once it paints. */
    if (document.activeElement !== target) {
      requestAnimationFrame(() => {
        if (this.hasAttribute('open') && !this.contains(document.activeElement)) {
          target.focus({ preventScroll: true });
        }
      });
    }
  }

  isFocusable(el) {
    return !el.hasAttribute('disabled') && !el.closest('[inert], [hidden]') && el.getClientRects().length > 0;
  }

  focusables() {
    return Array.from(
      this.querySelectorAll(
        'button, [href], input, select, textarea, summary, video[controls], audio[controls], iframe, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => el.getAttribute('tabindex') !== '-1' && this.isFocusable(el));
  }

  trapFocus(event) {
    const nodes = this.focusables();
    if (!nodes.length) {
      event.preventDefault();
      return;
    }
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    const active = document.activeElement;
    if (!this.contains(active)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
    } else if (event.shiftKey && (active === first || active === this.dialog)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  lock() {
    document.addEventListener('keydown', this.onKey);
    document.body.classList.add('daddl-overlay-open');
  }

  unlock() {
    document.removeEventListener('keydown', this.onKey);
    if (!document.querySelector('daddl-overlay[open]')) {
      document.body.classList.remove('daddl-overlay-open');
    }
  }
}

if (!customElements.get('daddl-overlay')) {
  customElements.define('daddl-overlay', DaddlOverlay);
}
